import { LinearProgress } from '@mui/material'
import BackComponent from 'components/back'
import ButtonMobileComponent from 'components/button-mobile'
import CardUserStudentComponent, { CardUserStudentActionProps } from 'components/card-user-student'
import { CarpoolManagementIcon, MessagingDotIcon, UserIcon } from 'components/icons'
import { AssignDriverIcon } from 'components/icons/AssignDriverIcon'
import { RemoveCircleIcon } from 'components/icons/RemcoveCircleIcon'
import ToastComponent from 'components/toast'
import WizardComponent from 'components/wizard'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { MessageProps } from 'types/CommonTypes'
import {
  CarpoolDriverStatus,
  CarpoolType,
  CommunityStudentCarpoolOutputProps,
  CommunityType,
  UpdateCommunityStudentCarpoolInputProps,
} from 'types/CommunityTypes'
import { RoomType, UserRoomsOutputProps } from 'types/RoomTypes'
import { RelationshipType, StudentProps } from 'types/StudentTypes'
import { UserOutputProps, UsersOutputProps } from 'types/UserTypes'
import {
  getCommunityStudentCarpools,
  updateCommunityStudentCarpoolDetail,
  updateCommunityStudentCarpools,
} from 'utils/communities'
import {
  formatLabel,
  generateCarpoolGroupName,
  getLocaleDate,
  isDateValid,
  isWeb,
  LABELS,
  titleCaseSchoolNameForUI,
} from 'utils/constants'
import { createRoom, getRooms } from 'utils/rooms'
import { getUsers } from 'utils/users'
import { isValidPhone } from 'utils/validations'
import './index.css'

interface CarpoolUserStudentProps {
  userId: string
  userName: string
  userRelationship: string
  userPhotoLink: string
  userCommunityStudentId: string
  userCommunityStudentName: string
  userRoomId?: string
}
interface CarpoolDetailProps {
  carpoolUserStudents: CarpoolUserStudentProps[]
  carpoolScheduledAt: string
  carpoolDriverStatus: CarpoolDriverStatus
  carpoolRoomId?: string
}

const CarpoolDetailComponent: React.FC<{
  carpoolType: CarpoolType
  carpoolDate: string
  carpoolDriverId: string
  fromPage: string
  studentId: string
  user: UserOutputProps
  userStudents: StudentProps[]
  onUpdate: (cb?: () => void) => void
  failedAvatarUrls: Set<string>
  onFailedAvatarUrl: (url: string) => void
}> = ({
  carpoolType,
  carpoolDate,
  carpoolDriverId,
  fromPage,
  studentId: myStudentId,
  user: { phoneNumber: myUserId },
  userStudents,
  onUpdate,
  failedAvatarUrls,
  onFailedAvatarUrl,
}) => {
  // 1. State Hooks
  const [communityUsers, setCommunityUsers] = useState<UsersOutputProps[] | undefined>(undefined)
  const [userRooms, setUserRooms] = useState<UserRoomsOutputProps[] | undefined>(undefined)
  const navigate = useNavigate()
  const [carpoolDetails, setCarpoolDetails] = useState<CarpoolDetailProps | null>(null)
  const [isLoading, setIsLoading] = useState<string>('Loading carpool details...')
  const [msg, setMsg] = useState<MessageProps>()

  // 2. Derived Constants
  const student: StudentProps | undefined = userStudents.find((s: StudentProps) => s.studentId === myStudentId)
  const comId = student?.schoolName
  const comType: CommunityType = CommunityType.SCHOOL
  const processCarpoolData = useCallback(
    (
      {
        carpoolDriverId,
        carpoolStudentIds,
        carpoolScheduledAt,
        carpoolDriverStatus,
      }: CommunityStudentCarpoolOutputProps,
      users: UsersOutputProps[],
      rooms: UserRoomsOutputProps[],
    ) => {
      // Helper functions
      const findUserStudent = (stuId: string): UsersOutputProps | undefined =>
        users.find((u: UsersOutputProps) => u.userCommunityStudentId === stuId)
      const findPrivateRoomId = (userId1: string, userId2: string): string | undefined => {
        const room = rooms.find(
          ({ roomType, roomMemberCount, roomMembers }: UserRoomsOutputProps) =>
            roomType === RoomType.PRIVATE &&
            roomMemberCount === 2 &&
            roomMembers?.length === 2 &&
            roomMembers?.includes(userId1) &&
            roomMembers?.includes(userId2),
        )
        return room?.roomId
      }
      const findCarpoolRoomId = (userIds: string[]): string | undefined => {
        const room = rooms.find(
          ({ roomType, roomMemberCount, roomAdmins }: UserRoomsOutputProps) =>
            roomType === RoomType.GROUP &&
            roomMemberCount === userIds.length &&
            roomAdmins?.length === userIds.length &&
            JSON.stringify([...roomAdmins].sort()) === JSON.stringify([...userIds].sort()),
        )
        return room?.roomId
      }

      const carpoolUserStudents = carpoolStudentIds.reduce((acc: CarpoolUserStudentProps[], stuId: string) => {
        const userStudent = findUserStudent(stuId)
        if (userStudent) {
          const {
            userId,
            userName,
            userRelationship,
            userPhotoLink,
            userCommunityStudentId,
            userCommunityStudentName,
          }: UsersOutputProps = userStudent
          const userRoomId = findPrivateRoomId(myUserId, userId)
          acc.push({
            userId,
            userName,
            userRelationship,
            userPhotoLink,
            userCommunityStudentId,
            userCommunityStudentName,
            userRoomId,
          })
        }
        return acc
      }, [])
      const carpoolRoomId = findCarpoolRoomId(carpoolUserStudents.map((u) => u.userId))

      if (carpoolUserStudents.length) {
        carpoolUserStudents.sort((a, b) => {
          // 1. Assign a "priority score" to each person. Lower scores go first.
          const getPriority = (uid: string) => {
            if (uid === myUserId) return 1 // Current user is always first
            if (uid === carpoolDriverId) return 2 // Driver is second
            return 3 // Everyone else is third
          }

          const priorityA = getPriority(a.userId)
          const priorityB = getPriority(b.userId)

          // 2. If priorities are different, sort by priority
          if (priorityA !== priorityB) {
            return priorityA - priorityB
          }

          // 3. If priorities are the same (e.g., two passengers), sort alphabetically
          // FIXME: Sort by student name instead of user name after we make student name mandatory
          return a.userName.localeCompare(b.userName)
        })

        setCarpoolDetails({ carpoolUserStudents, carpoolScheduledAt, carpoolDriverStatus, carpoolRoomId })
      } else setCarpoolDetails(null)
    },
    [myUserId],
  )

  // 3. Effect Hooks
  // Load carpool details whenever dependencies change
  useEffect(() => {
    if (!comId) return

    const loadData = async () => {
      setIsLoading('Loading users and rooms...')
      const [usersRes, roomsRes] = await Promise.all([getUsers(comId), getRooms()])
      if (usersRes.data) {
        setCommunityUsers((usersRes.data.users as UsersOutputProps[]) ?? [])
      }
      if (roomsRes.data) {
        setUserRooms((roomsRes.data.rooms as UserRoomsOutputProps[]) ?? [])
      }
      setIsLoading('')
    }

    loadData()
  }, [comId])
  useEffect(() => {
    if (!comId || !communityUsers?.length || !Array.isArray(userRooms)) return

    const loadCarpoolDetails = async (): Promise<void> => {
      setIsLoading('Loading carpool details...')
      const res = await getCommunityStudentCarpools(comId, myStudentId, {
        poolType: carpoolType,
        poolDriverId: carpoolDriverId,
        poolDate: carpoolDate,
      })
      if (res.data?.carpools?.length) {
        processCarpoolData(res.data.carpools[0], communityUsers, userRooms)
      } else setCarpoolDetails(null)
      setIsLoading('')
    }

    loadCarpoolDetails()
  }, [comId, myStudentId, carpoolType, carpoolDriverId, carpoolDate, communityUsers, userRooms, processCarpoolData])

  // 4. Derived State, Validations and Early Returns
  // Show loading state while data is being fetched
  if (!comId || carpoolDetails === undefined) return <LinearProgress />

  // Destructure with defaults to handle null/undefined cases
  const { carpoolUserStudents = [], carpoolScheduledAt = '', carpoolRoomId = '' } = carpoolDetails ?? {}

  // Format title with date
  let title = 'Carpool on '
  const date = new Date(carpoolScheduledAt || carpoolDate)
  const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(carpoolScheduledAt && {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }),
    timeZone: 'UTC',
  })
  title += dateTimeFormat.format(date)

  const footerBtnText = carpoolRoomId ? 'Open Group Chat' : 'Start Group Chat'
  // driver details
  const driverStudents = carpoolUserStudents?.filter((u) => u.userId === carpoolDriverId)
  const isYouDriver: boolean = myUserId === driverStudents?.[0]?.userId
  const carpoolDriverName = driverStudents?.[0]?.userName
  const carpoolDriverPhotoLink = driverStudents?.[0]?.userPhotoLink
  const carpoolDriverRoomId = driverStudents?.[0]?.userRoomId
  const carpoolDriverStudentId = driverStudents?.[0]?.userCommunityStudentId

  // Check if there's only a driver and no passengers
  const hasValidCarpool = carpoolUserStudents?.length > 1
  const hasOnlyDriver = carpoolUserStudents?.length === 1 && carpoolUserStudents?.[0]?.userId === carpoolDriverId

  // standard actions
  const onCancelCarpool = (): CardUserStudentActionProps => ({
    label: 'Cancel Carpool',
    icon: <RemoveCircleIcon />,
    modal: {
      title: 'Cancel this Carpool?',
      body: 'All passengers will be removed and notified. This action cannot be undone. Are you sure?',
      actionText: 'Confirm Cancellation',
    },
    onClick: () => handleCancelCarpool(),
  })
  const onMessageUser = (uid: string, roomId?: string): CardUserStudentActionProps => ({
    label: 'Message User',
    icon: <MessagingDotIcon color='#5F6D7E' size={24} />,
    onClick: () => handleMessageUser(uid, roomId),
  })
  const onLeaveCarpool = (): CardUserStudentActionProps => ({
    label: 'Leave Carpool',
    icon: <RemoveCircleIcon />,
    modal: {
      title: 'Leave this Carpool?',
      body: `The driver will be notified that your child is no longer carpooling.`,
      actionText: 'Leave',
    },
    onClick: () => handleLeaveCarpool(),
  })
  // early return
  if (isLoading) return <LinearProgress />

  // 5. Event Handlers
  const handleReturnToCarpoolManagement = () => {
    if (fromPage === 'carpool-management') {
      // Go back to the management page and remove this page from history
      navigate(-1)
    } else {
      // Otherwise, redirect to the management page replacing this page from history
      navigate(`/carpool/management?id=${myStudentId}`, { replace: true })
    }
  }
  const handleMessageGroup = async (roomId?: string) => {
    if (roomId) navigate(`/messaging/${roomId}`)
    else {
      // Create private chat room
      const roomUsers = carpoolUserStudents.map((u) => u.userId)
      const roomName = generateCarpoolGroupName()
      const roomDescription = `${titleCaseSchoolNameForUI(comId)} ${LABELS[comType]?.carpoolType?.[carpoolType]?.lowercase ?? formatLabel(carpoolType, 'lowercase')} carpool for ${carpoolDate} created on ${getLocaleDate()}.`
      const { msg, data, error } = await createRoom({
        roomType: RoomType.GROUP,
        roomUsers,
        roomName,
        roomDescription,
        roomAdmins: roomUsers,
        roomCommunityId: comId,
      })
      if (error) setMsg(msg)
      else if (data) {
        onUpdate(() => navigate(`/messaging/${data.roomId}`))
      }
    }
  }
  const handleMessageUser = async (userId: string, roomId?: string) => {
    if (roomId) navigate(`/messaging/${roomId}`)
    else {
      // Create private chat room
      const { msg, data, error } = await createRoom({ roomType: RoomType.PRIVATE, roomUsers: [myUserId, userId] })
      if (error) setMsg(msg)
      else if (data) {
        onUpdate(() => navigate(`/messaging/${data.roomId}`))
      }
    }
  }
  const handleCancelCarpool = async () => {
    setIsLoading(`Updating carpool...`)
    const body: UpdateCommunityStudentCarpoolInputProps = {
      carpoolType,
      carpools: {
        [carpoolDate]: {
          time: -1,
          driverId: '',
        },
      },
    }
    const res = await Promise.all(
      driverStudents.map(({ userCommunityStudentId: studId }) => updateCommunityStudentCarpools(comId, studId, body)),
    )
    if (res.some((r) => r.error)) {
      setMsg(res.find((r) => r.error)?.msg)
      setIsLoading('')
    } else {
      onUpdate(() => navigate(-1))
    }
  }
  const handleAssignDriver = async (userId: string) => {
    setIsLoading(`Updating carpool driver...`)
    const { msg, error } = await updateCommunityStudentCarpoolDetail(comId, myStudentId, {
      carpoolType,
      assignCarpoolDrivers: {
        [carpoolDate]: userId,
      },
    })
    if (error) {
      setMsg(msg)
      setIsLoading('')
    } else {
      await onUpdate(() =>
        navigate(`/carpool/detail/${carpoolType}/${carpoolDate}?id=${myStudentId}`, {
          state: { userId },
          replace: true,
        }),
      )
    }
  }
  const handleRemovePassenger = async (stuId: string) => {
    setIsLoading(`Updating carpool passenger...`)
    const { msg, error } = await updateCommunityStudentCarpoolDetail(comId, myStudentId, {
      carpoolType,
      removeCarpoolPassengers: {
        [carpoolDate]: Array.from(new Set<string>([stuId])),
      },
    })
    if (error) {
      setMsg(msg)
      setIsLoading('')
    } else {
      await onUpdate(() => navigate(0))
    }
  }
  const handleLeaveCarpool = async (stuId: string = myStudentId) => {
    setIsLoading(`Updating carpool...`)
    const body: UpdateCommunityStudentCarpoolInputProps = {
      carpoolType,
      carpools: {
        [carpoolDate]: {
          time: -1,
          driverId: '',
        },
      },
    }
    const { msg, error } = await updateCommunityStudentCarpools(comId, myStudentId, body)
    if (error) {
      setMsg(msg)
      setIsLoading('')
    } else {
      await onUpdate(() => {
        setMsg({ style: 'success', text: `You have left the carpool.`, duration: 3000 })
        navigate(-1)
        setIsLoading('')
      })
    }
  }
  const handleViewProfile = async (userId: string, stuId: string) =>
    navigate(`/userpool/${comId}?studentId=${myStudentId}&userStudentId=${stuId}`, { state: { userId } })

  // 6. Render
  const body: JSX.Element =
    hasValidCarpool || hasOnlyDriver ? (
      <div className={`flex flex-col flex-1 self-stretch gap-2 ${isWeb ? '' : 'px-5'}`}>
        {/* Driver Section */}
        <div>
          <h3 className='text-sm font-semibold mb-2'>DRIVER</h3>
          <CardUserStudentComponent
            userName={isYouDriver ? 'You' : carpoolDriverName}
            photoLink={carpoolDriverPhotoLink}
            failedPhotoLinks={failedAvatarUrls}
            onFailedPhotoLink={onFailedAvatarUrl}
            actions={isYouDriver ? [onCancelCarpool()] : [onMessageUser(carpoolDriverId, carpoolDriverRoomId)]}
            onClick={isYouDriver ? undefined : () => handleViewProfile(carpoolDriverId, carpoolDriverStudentId)}
          />
        </div>

        {/* Passengers Section */}
        <div>
          <div className='flex items-center gap-2 mt-4'>
            <UserIcon />
            <h3 className='text-base font-semibold'>Carpooling Families ({carpoolUserStudents.length})</h3>
          </div>
          {isYouDriver && (
            <div className='text-sm text-disable'>{'Manage your passengers and reassign drivers below'}</div>
          )}

          <div className='w-full flex flex-col items-start mt-2 lg:gap-2'>
            {carpoolUserStudents.map((carpoolUserStudent, index) => {
              const {
                userId,
                userName,
                userRelationship,
                userPhotoLink,
                userCommunityStudentId: stuId,
                userCommunityStudentName,
                userRoomId,
              } = carpoolUserStudent
              const isYou: boolean = userId === myUserId
              const isDriver: boolean = userId === carpoolDriverId
              const studentName = userCommunityStudentName || 'Student'
              // Driver students can cancel carpool (if they are the only current user), current user students can leave carpool, and other student user profiles can be viewed
              const passengerActions: CardUserStudentActionProps[] = isYou
                ? isYouDriver && driverStudents.length === 1
                  ? [onCancelCarpool()]
                  : [onLeaveCarpool()]
                : [onMessageUser(userId, userRoomId)]
              // However, if you are the driver, then extra actions are available when viewing other users
              if (isYouDriver && !isYou && !isDriver) {
                passengerActions.push(
                  {
                    label: 'Assign Driver',
                    icon: <AssignDriverIcon />,
                    modal: {
                      title: `Make ${userName.split(' ')[0]} the New Driver?`,
                      body: `This will transfer driving responsibility to them. All passengers will be notified.`,
                      actionText: 'Assign',
                    },
                    onClick: () => handleAssignDriver(userId),
                  },
                  {
                    label: 'Remove from Carpool',
                    icon: <RemoveCircleIcon />,
                    modal: {
                      title: `Remove ${studentName || 'student'} from carpool?`,
                      body: `The ${userRelationship === RelationshipType.OTHER ? 'parent/guardian' : userRelationship.toLowerCase()} will be notified.`,
                      actionText: 'Remove',
                    },
                    onClick: () => handleRemovePassenger(stuId),
                  },
                )
              }
              // FIXME: Show student name above user name and relationship after we make student name mandatory
              return (
                <CardUserStudentComponent
                  key={index}
                  userName={
                    isYou && isDriver ? 'You (Driver)' : isYou ? 'You' : isDriver ? `${userName} (Driver)` : userName
                  }
                  avatarSize={8}
                  photoLink={userPhotoLink}
                  failedPhotoLinks={failedAvatarUrls}
                  onFailedPhotoLink={onFailedAvatarUrl}
                  studentName={studentName}
                  actions={passengerActions}
                  onClick={isYou ? undefined : () => handleViewProfile(userId, stuId)}
                />
              )
            })}
          </div>
        </div>

        {/* Back to Carpool Management Link */}
        <div className='mt-4 text-center text-sm text-gray-600'>
          <button onClick={handleReturnToCarpoolManagement}>Return to Carpool Management</button>
        </div>
      </div>
    ) : (
      <div className='flex flex-col items-center justify-center p-8 text-center'>
        <CarpoolManagementIcon size={40} />
        <h2 className='text-2xl font-semibold mt-4'>Carpool Not Found</h2>
      </div>
    )

  // The footer with the primary action is defined once
  const footer: JSX.Element = hasValidCarpool ? (
    <ButtonMobileComponent
      text={footerBtnText}
      color='bg-custom-yellow'
      width='w-full'
      onClick={() => handleMessageGroup(carpoolRoomId)}
      iconPosition='left'
      icon={<MessagingDotIcon />}
    />
  ) : hasOnlyDriver ? (
    <div className='flex flex-col items-center p-4 text-center bg-white'>
      <p className='text-gray-600 mb-4'>
        This carpool is currently just you! Invite other parents to join your carpool by sending them a message.
        Coordinate schedules and share rides with families in your community.
      </p>
      <ButtonMobileComponent
        text='Invite Parents'
        color='bg-custom-yellow'
        width='auto'
        onClick={() => navigate(`/messaging?action=createRoom`)}
        iconPosition='left'
        icon={<MessagingDotIcon />}
      />
    </div>
  ) : (
    <div className='flex flex-col items-center p-4 text-center bg-white'>
      <p className='text-gray-600 mb-4'>
        The carpool you&apos;re looking for may have been cancelled or the link might be outdated. Please return to
        Carpool Management to view your carpools or create a new one.
      </p>
      <ButtonMobileComponent
        text='Return to Carpool Management'
        color='bg-custom-yellow'
        width='auto'
        onClick={handleReturnToCarpoolManagement}
        iconPosition='left'
        icon={<CarpoolManagementIcon />}
      />
    </div>
  )

  return (
    <div className='flex flex-col min-h-screen'>
      {/***************** Display progress and error *****************/}
      {msg && <ToastComponent {...msg} onClose={() => setMsg(undefined)} />}
      {!isWeb ? (
        <div className='flex flex-col flex-1'>
          <div className='sticky top-0 z-10 bg-white'>
            <BackComponent text={title} />
          </div>
          <div className='flex-1 pb-24'>{body}</div>
          <div className='fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 pt-3 pb-7 px-5'>{footer}</div>
        </div>
      ) : (
        <WizardComponent title={title}>
          <>
            {body}
            {footer}
          </>
        </WizardComponent>
      )}
    </div>
  )
}

function Index({
  user,
  students,
  onUpdate,
  failedAvatarUrls,
  onFailedAvatarUrl,
}: {
  user?: UserOutputProps
  students: StudentProps[]
  onUpdate: (cb?: () => void) => void
  failedAvatarUrls: Set<string>
  onFailedAvatarUrl: (url: string) => void
}): JSX.Element {
  // Build props for child component
  const params = useParams()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const poolDriverId: string = location.state?.userId ?? params?.poolDriverId ?? ''
  const fromPage: string = location.state?.fromPage ?? ''
  const poolType: CarpoolType | undefined = params?.poolType
  const poolDate: string | undefined = params?.poolDate
  const studentId = searchParams.get('id') ?? ''
  const student: StudentProps | undefined = students?.find((s: StudentProps) => s.studentId === studentId)

  if (
    !poolType ||
    !poolDate ||
    !isDateValid(poolDate) ||
    !poolDriverId ||
    !isValidPhone(poolDriverId, true) ||
    !user ||
    !studentId ||
    !student
  )
    return <LinearProgress />

  // 5. Event Handlers

  // 6. Render
  return (
    <CarpoolDetailComponent
      carpoolType={poolType}
      carpoolDate={poolDate}
      carpoolDriverId={poolDriverId}
      fromPage={fromPage}
      studentId={studentId}
      user={user}
      userStudents={students}
      onUpdate={onUpdate}
      failedAvatarUrls={failedAvatarUrls}
      onFailedAvatarUrl={onFailedAvatarUrl}
    />
  )
}

export default Index
